<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { accountCurrency } from 'dashboard/helper/currency';
import subDays from 'date-fns/subDays';
import ReportHeader from './components/ReportHeader.vue';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import ActiveFilterChip from './components/Filters/v3/ActiveFilterChip.vue';
import EventLocationsMap from './components/EventLocationsMap.vue';

const { t } = useI18n();
const store = useStore();
const { currentAccount } = useAccount();

const uiFlags = useMapGetter('getOverviewUIFlags');
const detailedMetric = useMapGetter('getPlanningEventDetailedMetric');
const contactLocations = useMapGetter('getPlanningEventContactLocations');

const i18nKey = 'EVENTS_REPORTS';

const STAGE_OPTIONS = [
  { id: 'all', labelKey: 'MAP_FILTER_ALL' },
  { id: 'inquiry', labelKey: 'INQUIRIES' },
  { id: 'confirmed', labelKey: 'CONFIRMED' },
  { id: 'completed', labelKey: 'COMPLETED' },
  { id: 'lost', labelKey: 'DEALS_LOST' },
  { id: 'draft', labelKey: 'DRAFTS' },
];

const dateRange = ref([subDays(new Date(), 30), new Date()]);
const selectedStage = ref('all');
const selectedSpaceId = ref('all');
const showMenu = ref(false);
const activeFilterType = ref('');

const buildParams = () => {
  const [start, end] = dateRange.value;
  const params = {};
  if (start) params.since = start.toISOString();
  if (end) params.until = end.toISOString();
  return params;
};

const fetchData = () => {
  const params = buildParams();
  store.dispatch('fetchPlanningEventDetailedMetric', params);
  store.dispatch('fetchPlanningEventContactLocations', params);
};

const handleDateChange = value => {
  dateRange.value = value;
  fetchData();
};

const stageOptions = computed(() =>
  STAGE_OPTIONS.map(option => ({
    id: option.id,
    name: t(`${i18nKey}.${option.labelKey}`),
    type: 'stage',
  }))
);

const spaces = computed(() => contactLocations.value?.spaces || []);

const spaceOptions = computed(() => [
  {
    id: 'all',
    name: t(`${i18nKey}.MAP_FILTER_ALL_SPACES`),
    type: 'space',
  },
  ...spaces.value.map(space => ({
    id: space.id,
    name: space.name,
    type: 'space',
  })),
]);

const selectedStageName = computed(() => {
  const option = STAGE_OPTIONS.find(item => item.id === selectedStage.value);
  return t(`${i18nKey}.${option?.labelKey || 'MAP_FILTER_ALL'}`);
});

const selectedSpaceName = computed(() => {
  if (selectedSpaceId.value === 'all') {
    return t(`${i18nKey}.MAP_FILTER_ALL_SPACES`);
  }
  const space = spaces.value.find(item => item.id === selectedSpaceId.value);
  return space?.name || t(`${i18nKey}.MAP_FILTER_SPACE`);
});

const openDropdown = filterType => {
  if (activeFilterType.value === filterType && showMenu.value) {
    closeDropdown();
    return;
  }
  activeFilterType.value = filterType;
  showMenu.value = true;
};

const closeDropdown = () => {
  showMenu.value = false;
  activeFilterType.value = '';
};

const selectStage = item => {
  selectedStage.value = item.id;
  closeDropdown();
};

const selectSpace = item => {
  selectedSpaceId.value = item.id;
  closeDropdown();
};

const formatCurrency = value => {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: accountCurrency(currentAccount.value),
    minimumFractionDigits: 2,
  }).format(value || 0);
};

const metrics = computed(() => [
  {
    label: t(`${i18nKey}.TOTAL_EVENTS`),
    value: detailedMetric.value.total_events || 0,
  },
  {
    label: t(`${i18nKey}.TOTAL_REVENUE`),
    value: formatCurrency(detailedMetric.value.total_revenue),
  },
  {
    label: t(`${i18nKey}.AVERAGE_REVENUE`),
    value: formatCurrency(detailedMetric.value.average_revenue),
  },
  {
    label: t(`${i18nKey}.CONVERSION_RATE`),
    value: `${detailedMetric.value.conversion_rate || 0}%`,
  },
]);

const stageMetrics = computed(() => [
  {
    label: t(`${i18nKey}.INQUIRIES`),
    value: detailedMetric.value.inquiries || 0,
  },
  {
    label: t(`${i18nKey}.CONFIRMED`),
    value: detailedMetric.value.confirmed || 0,
  },
  {
    label: t(`${i18nKey}.COMPLETED`),
    value: detailedMetric.value.completed || 0,
  },
  {
    label: t(`${i18nKey}.DEALS_LOST`),
    value: detailedMetric.value.deals_lost || 0,
  },
  {
    label: t(`${i18nKey}.DRAFTS`),
    value: detailedMetric.value.drafts || 0,
  },
]);

const revenueByStage = computed(() => {
  const data = detailedMetric.value.revenue_by_stage || {};
  return Object.entries(data).map(([stage, amount]) => ({
    stage,
    amount: formatCurrency(amount),
  }));
});

onMounted(() => {
  fetchData();
});
</script>

<template>
  <ReportHeader :header-title="t(`${i18nKey}.HEADER`)" />
  <div class="flex flex-col gap-4 pb-6">
    <div class="flex flex-col w-full gap-3 lg:flex-row print:hidden">
      <WootDatePicker
        :value="dateRange"
        :confirm-text="t('REPORT.CUSTOM_DATE_RANGE.CONFIRM')"
        :placeholder="t('REPORT.CUSTOM_DATE_RANGE.PLACEHOLDER')"
        range
        @change="handleDateChange"
      />

      <div class="flex flex-wrap gap-2 items-center">
        <ActiveFilterChip
          filled
          menu-align="start"
          icon="i-lucide-layers"
          :id="selectedStage"
          :name="selectedStageName"
          type="stage"
          :options="stageOptions"
          :active-filter-type="activeFilterType"
          :show-menu="showMenu"
          :placeholder="t(`${i18nKey}.MAP_SEARCH_STAGE`)"
          :show-clear-filter="false"
          @toggle-dropdown="openDropdown"
          @close-dropdown="closeDropdown"
          @add-filter="selectStage"
        />

        <ActiveFilterChip
          v-if="spaceOptions.length > 1"
          filled
          menu-align="start"
          icon="i-lucide-building-2"
          :id="selectedSpaceId"
          :name="selectedSpaceName"
          type="space"
          :options="spaceOptions"
          :active-filter-type="activeFilterType"
          :show-menu="showMenu"
          :placeholder="t(`${i18nKey}.MAP_SEARCH_SPACE`)"
          :show-clear-filter="false"
          enable-search
          @toggle-dropdown="openDropdown"
          @close-dropdown="closeDropdown"
          @add-filter="selectSpace"
        />
      </div>
    </div>

    <!-- Loading -->
    <div
      v-if="uiFlags.isFetchingPlanningEventDetailedMetric"
      class="flex items-center justify-center py-12"
    >
      <span class="text-n-slate-11">{{ t(`${i18nKey}.LOADING_MESSAGE`) }}</span>
    </div>

    <template v-else>
      <!-- Top-level metrics -->
      <div
        class="grid grid-cols-2 md:grid-cols-4 gap-4 p-6 rounded-xl shadow outline-1 outline outline-n-container bg-n-solid-2"
      >
        <div v-for="metric in metrics" :key="metric.label" class="min-w-0">
          <h3 class="text-sm text-n-slate-11">
            {{ metric.label }}
          </h3>
          <p class="text-n-slate-12 text-2xl font-semibold mb-0 mt-1">
            {{ metric.value }}
          </p>
        </div>
      </div>

      <!-- Stage breakdown -->
      <div
        class="p-6 rounded-xl shadow outline-1 outline outline-n-container bg-n-solid-2"
      >
        <h4 class="text-lg font-medium text-n-slate-12 mb-4">
          {{ t(`${i18nKey}.STAGE_BREAKDOWN`) }}
        </h4>
        <div class="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div
            v-for="metric in stageMetrics"
            :key="metric.label"
            class="min-w-0"
          >
            <h3 class="text-sm text-n-slate-11">
              {{ metric.label }}
            </h3>
            <p class="text-n-slate-12 text-2xl font-semibold mb-0 mt-1">
              {{ metric.value }}
            </p>
          </div>
        </div>
      </div>

      <!-- Contact locations map -->
      <EventLocationsMap
        :locations="contactLocations.locations || []"
        :spaces="contactLocations.spaces || []"
        :loading="uiFlags.isFetchingPlanningEventContactLocations"
        :selected-stage="selectedStage"
        :selected-space-id="selectedSpaceId"
      />

      <!-- Revenue by stage -->
      <div
        v-if="revenueByStage.length"
        class="p-6 rounded-xl shadow outline-1 outline outline-n-container bg-n-solid-2"
      >
        <h4 class="text-lg font-medium text-n-slate-12 mb-4">
          {{ t(`${i18nKey}.REVENUE_BY_STAGE`) }}
        </h4>
        <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div v-for="item in revenueByStage" :key="item.stage" class="min-w-0">
            <h3 class="text-sm text-n-slate-11 capitalize">
              {{ item.stage }}
            </h3>
            <p class="text-n-slate-12 text-2xl font-semibold mb-0 mt-1">
              {{ item.amount }}
            </p>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
